import { ArrowLeftIcon, Button, LoadSpinner } from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import { transformRustyPotentialBookingtoFilters } from '@sales-app/context/workspace/homeFilters.util'
import { WorkspaceAction } from '@sales-app/context/workspace/workspace.actions'
import { useWorkspaceContext } from '@sales-app/context/workspace/workspace.hooks'
import { WorkspaceView } from '@sales-app/context/workspace/workspace.type'
import { getWorkspacePath } from '@sales-app/context/workspace/workspace.util'
import { PotentialBookingQueries } from '@sales-app/graphql/queries/potentialBooking/potentialBookings.queries'
import { StatusQueries } from '@sales-app/graphql/queries/potentialBooking/status.queries'
import { BottomBar } from '@sales-app/views/BottomBar/BottomBar'
import { BookablePersonHeader } from '@sales-app/views/Shared/BookablePersonHeader'
import { WorkspaceTabs } from '@sales-app/views/Workspace/WorkspaceNavigation/WorkspaceTabs'
import clsx from 'clsx'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { BottomSheet, History, NoResults } from '@/components'
import { useUserContext } from '@/context'
import { AgentRoles } from '@/context/user/user.types'
import { RecordType } from '@/graphql/queries/fieldHistoryLogs/fieldHistoryLogs.queries'
import { useMobile, useToggle } from '@/hooks'
import { useFieldHistoryLogs } from '@/hooks/useFieldHistoryLogs/useFieldHistoryLogs'
import { useGraphql } from '@/hooks/useGraphql'

import { ActivitySection } from './ActivitySection/ActivitySection'
import { BookingSectionTabs } from './BookingSectionTabs'
import { ControlSection } from './ControlSection/ControlSection'
import { StatusSelection } from './ControlSection/Filters/StatusSelection'

export const PotentialBooking = () => {
  const { user } = useUserContext()
  const [selectedTab, setSelectedTab] = useState<BookingSections>(BookingSections.Details)
  const [{ tabs, activeTabId, activeTabType }, workspaceDispatch] = useWorkspaceContext()
  const { potentialBookingId } = useParams<{ potentialBookingId: string }>()
  const { refetch, data, isLoading, isFetching } = useGraphql<PotentialBookingQueries.GET_POTENTIAL_BOOKING.Response>(
    PotentialBookingQueries.getPotentialBooking.name,
    PotentialBookingQueries.getPotentialBooking(),
    { id: Number(potentialBookingId) },
    {
      enabled: !!potentialBookingId && !!Number(potentialBookingId),
      refetchOnMount: 'always',
    },
  )

  const { data: potentialBookingStatusData } = useGraphql<StatusQueries.GET_STATUSES.Response>(
    StatusQueries.getStatuses.name,
    StatusQueries.getStatuses(),
    {
      filter: {
        active: true,
        standard: true,
      },
    },
  )

  const potentialBookingStatuses = useMemo(() => {
    return potentialBookingStatusData?.potentialBookingStatuses
      ? potentialBookingStatusData.potentialBookingStatuses
      : []
  }, [potentialBookingStatusData])

  const activeWorkspaceTab =
    activeTabId && activeTabType
      ? tabs.find((t) => t.bookablePersonId === activeTabId && t.bookablePersonType === activeTabType)
      : undefined

  const { refetch: fieldHistoryRefetch } = useFieldHistoryLogs({
    record: { id: Number(potentialBookingId), recordType: RecordType.POTENTIAL_BOOKINGS },
    enabled: !!potentialBookingId && !!Number(potentialBookingId),
  })

  const currentFilters = useMemo(() => {
    return data?.potentialBooking
      ? transformRustyPotentialBookingtoFilters(data?.potentialBooking, { listView: true })
      : { listView: true }
  }, [data?.potentialBooking])

  useEffect(() => {
    workspaceDispatch(WorkspaceAction.updateFilters({ ...currentFilters }))
  }, [workspaceDispatch, currentFilters])

  const { isShown: modalShown, toggle: modalToggle } = useToggle()
  const navigate = useNavigate()
  const changeToSummaryView = () => {
    if (activeWorkspaceTab) {
      const { bookablePersonId, bookablePersonType } = activeWorkspaceTab
      workspaceDispatch(WorkspaceAction.updateTab({ ...activeWorkspaceTab, view: WorkspaceView.SUMMARY }))
      const path = getWorkspacePath({
        view: WorkspaceView.SUMMARY,
        type: bookablePersonType,
        id: bookablePersonId,
        potentialBookingId: null,
      })

      navigate(path)
    }
  }

  useEffect(() => {
    if (data?.potentialBooking?.bookablePerson && !isFetching) {
      if (
        data.potentialBooking.bookablePerson.bookablePersonId !== activeWorkspaceTab?.bookablePersonId ||
        data.potentialBooking.bookablePerson.bookablePersonType !== activeWorkspaceTab?.bookablePersonType
      ) {
        workspaceDispatch(
          WorkspaceAction.openTab({
            bookablePersonId: data.potentialBooking.bookablePerson.bookablePersonId,
            bookablePersonType: data.potentialBooking.bookablePerson.bookablePersonType,
            label: data.potentialBooking.bookablePerson.fullName + '',
            lastAccessedTime: new Date(),
            potentialBookingId: data.potentialBooking.id,
            potentialBooking: data.potentialBooking,
            view: WorkspaceView.POTENTIALBOOKING,
            potentialBookingFilters: currentFilters,
            userId: data.potentialBooking.bookablePerson.userId,
          }),
        )
      } else if (activeWorkspaceTab) {
        if (
          activeWorkspaceTab.view !== WorkspaceView.POTENTIALBOOKING ||
          data.potentialBooking?.id !== activeWorkspaceTab.potentialBooking?.id ||
          data.potentialBooking?.id !== activeWorkspaceTab.potentialBookingId ||
          data.potentialBooking.potentialBookingStatusId !==
            activeWorkspaceTab.potentialBooking?.potentialBookingStatusId
        ) {
          workspaceDispatch(
            WorkspaceAction.updateTab({
              ...activeWorkspaceTab,
              view: WorkspaceView.POTENTIALBOOKING,
              potentialBooking: data.potentialBooking,
              potentialBookingId: data.potentialBooking.id,
              label: data.potentialBooking.bookablePerson.fullName + '',
              userId: data.potentialBooking.bookablePerson.userId,
            }),
          )
        }
      }
    }
  }, [data, currentFilters, activeWorkspaceTab, workspaceDispatch, isFetching])

  const { isTablet, isMobile } = useMobile()
  const showTabView = isTablet || isMobile

  const handleRefetch = () => {
    potentialBookingId && refetch()
    potentialBookingId && fieldHistoryRefetch()
  }

  const validateDisableStatus = (s?: string) => {
    return s === 'Not interested' || s === 'Booked' || s === 'Approved'
  }

  const isSupervisor = user?.agent?.role === AgentRoles.SUPERVISOR

  const isControlSectionDisabled =
    validateDisableStatus(data?.potentialBooking?.potentialBookingStatus) && !isSupervisor

  const { bookablePersonType, bookablePersonId } = data?.potentialBooking?.bookablePerson || {}

  return (
    <div className="flex w-full flex-col p-4 md:p-8">
      {tabs && tabs.length > 0 ? (
        <>
          <WorkspaceTabs />
          <div className="flex h-full w-full">
            <div
              className="flex w-full flex-1 flex-col pb-2 pt-4 md:px-2 md:pb-0"
              key={`${bookablePersonType} - ${bookablePersonId} - ${data?.potentialBooking?.id}`}
            >
              {data?.potentialBooking?.id && data.potentialBooking.bookablePerson && (
                <div className="mb-2 flex flex-col w-full px-6 md:mb-2 md:px-0">
                  <div className="flex w-full flex-col md:flex-row justify-between items-start">
                    <div className="flex w-full md:w-1/2">
                      <BookablePersonHeader
                        bookablePerson={data.potentialBooking.bookablePerson}
                        hasDuplicates={data.potentialBooking.bookablePerson.hasDuplicates}
                        isDuplicatesLoading={false}
                        refetch={refetch}
                      />
                    </div>

                    <div className="flex w-full mb-3 md:w-1/2">
                      <div className="flex mr-4 justify-between items-center">
                        Booking ID: {data.potentialBooking.id}
                      </div>

                      <StatusSelection
                        currentFilters={activeWorkspaceTab?.potentialBookingFilters || { listView: true }}
                        isSupervisor={isSupervisor}
                        potentialBookingStatuses={potentialBookingStatuses}
                      />
                      <div className="flex justify-between">
                        <div className="flex items-end">
                          <Button className="ml-4 bg-grey-100" onClick={modalToggle} size="small" variant="secondary">
                            View Booking History
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <Button
                      className="mr-w flex h-8 items-center rounded-full md:mb-0 md:justify-center md:bg-grey-100"
                      onClick={changeToSummaryView}
                      size="small"
                      variant="secondary"
                    >
                      <div className="flex justify-center items-center">
                        <ArrowLeftIcon className="h-4" />
                        <span>Back to Bookable Person</span>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              <BookingSectionTabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
              <Container className="flex w-full flex-row px-4 md:px-0">
                {activeWorkspaceTab && (
                  <>
                    {showTabView ? (
                      <>
                        {selectedTab === BookingSections.Details && (
                          <ControlSection
                            activeWorkspaceTab={activeWorkspaceTab}
                            currentFilters={currentFilters}
                            disabled={isControlSectionDisabled}
                            isLoading={isLoading}
                            refetchDetails={handleRefetch}
                          />
                        )}
                        {selectedTab === BookingSections.Activity && (
                          <ActivitySection
                            bookablePerson={activeWorkspaceTab}
                            potentialBookingStatuses={potentialBookingStatuses}
                            refetchPotentialBooking={refetch}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <ControlSection
                          activeWorkspaceTab={activeWorkspaceTab}
                          currentFilters={currentFilters}
                          disabled={isControlSectionDisabled}
                          isLoading={isLoading}
                          refetchDetails={handleRefetch}
                        />
                        <div className="flex w-full flex-1 flex-col overflow-visible overflow-y-scroll ">
                          {isLoading ? (
                            <div className="flex h-48 w-full items-center justify-center">
                              <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
                            </div>
                          ) : (
                            <ActivitySection
                              bookablePerson={activeWorkspaceTab}
                              potentialBookingStatuses={potentialBookingStatuses}
                              refetchPotentialBooking={refetch}
                            />
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}
              </Container>

              <BottomSheet isVisible={modalShown} mobileFullHeight={true} onClose={modalToggle}>
                {potentialBookingId && Number(potentialBookingId) && (
                  <History recordId={Number(potentialBookingId)} recordType={RecordType.POTENTIAL_BOOKINGS} />
                )}
              </BottomSheet>
            </div>
          </div>
        </>
      ) : (
        <NoResults />
      )}
      <BottomBar />
    </div>
  )
}

const Container = styled.div`
  min-height: calc((var(--vh) * 100) - 8rem);
`

export enum BookingSections {
  Activity = 'Activity',
  Details = 'Details',
}

export const SectionContainer = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div {...props} className={clsx('md:border-secondary rounded-2xl md:px-4 md:py-4 md:border', className)} />
)
